import React, { useEffect, useMemo, useState } from "react";
import { Search, PackageCheck, Store, MessageCircle } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const idr = (n) => `Rp${(Number(n) || 0).toLocaleString("id-ID")}`;
const STATUS = {
  Ready: { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  Menipis: { cls: "bg-amber-100 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  Habis: { cls: "bg-rose-100 text-rose-600 border-rose-200", dot: "bg-rose-400" },
};
const STATUS_FILTERS = ["all", "Ready", "Menipis", "Habis"];

export default function PublicStock() {
  const [meta, setMeta] = useState({ app_name: "Ventedaily", logo_url: "", categories: [] });
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);
  const limit = 24;

  useEffect(() => { api.get("/public/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => ({ q: debounced || undefined, category: category !== "all" ? category : undefined, status: status !== "all" ? status : undefined, page, limit }), [debounced, category, status, page]);
  useEffect(() => {
    setLoading(true);
    api.get("/public/stock", { params }).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [params]);

  const waLink = (it) => {
    const msg = encodeURIComponent(`Halo ${meta.app_name}, saya mau order:\n${it.product_name} - ${it.color} ${it.size}\nStatus: ${it.status}`);
    const num = (meta.whatsapp || "").replace(/[^0-9]/g, "");
    return num ? `https://wa.me/${num}?text=${msg}` : `https://api.whatsapp.com/send?text=${msg}`;
  };

  return (
    <div className="min-h-screen bg-[#f6efe6]" data-testid="public-stock-page">
      {/* Header */}
      <header className="bg-[#3f2d20] text-[#f6efe6] sticky top-0 z-20 shadow-md">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#f6efe6]/15 flex items-center justify-center overflow-hidden">
            {meta.logo_url ? <img src={meta.logo_url} alt="" className="w-full h-full object-contain p-1" /> : <Store className="w-6 h-6" />}
          </div>
          <div>
            <div className="font-heading font-bold text-lg leading-tight">{meta.app_name}</div>
            <div className="text-[12px] text-[#f6efe6]/60">Cek Stok Reseller</div>
          </div>
          <span className="ml-auto text-[11px] text-[#f6efe6]/50 hidden sm:block">Update realtime · tanpa login</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5">
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3.5 top-3.5 w-5 h-5 text-[#8a7963]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama produk, warna, atau ukuran..."
            className="pl-11 h-12 text-base bg-white border-[#e3d7c6] rounded-xl shadow-sm" data-testid="public-search" autoFocus />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-4">
          {STATUS_FILTERS.map((s) => (
            <button key={s} onClick={() => { setStatus(s); setPage(1); }} data-testid={`public-status-${s}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${status === s ? "bg-[#3f2d20] text-[#f6efe6] border-[#3f2d20]" : "bg-white text-[#5a4634] border-[#e3d7c6] hover:border-[#c9b28f]"}`}>
              {s === "all" ? "Semua Status" : s}
            </button>
          ))}
          {meta.categories.length > 0 && (
            <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} data-testid="public-category"
              className="ml-auto px-3 py-1.5 rounded-full text-sm border border-[#e3d7c6] bg-white text-[#5a4634] outline-none">
              <option value="all">Semua Kategori</option>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>

        <div className="text-sm text-[#8a7963] mb-3" data-testid="public-count">{loading ? "Memuat..." : `${data.total} varian ditemukan`}</div>

        {/* List */}
        {!loading && data.items.length === 0 ? (
          <div className="text-center py-16 text-[#8a7963]">
            <PackageCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
            Produk tidak ditemukan. Coba kata kunci lain.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.items.map((it) => {
              const s = STATUS[it.status] || STATUS.Habis;
              return (
                <div key={it.id} data-testid={`public-item-${it.id}`}
                  className="bg-white rounded-xl border border-[#ece2d3] p-3.5 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-[#3f2d20] leading-snug">{it.product_name}</div>
                      <div className="text-[13px] text-[#8a7963] mt-0.5">{it.color} · {it.size}</div>
                      {it.category && <div className="text-[11px] text-[#b0a08c] mt-0.5">{it.category}</div>}
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${s.cls}`} data-testid={`public-status-badge-${it.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{it.status}
                    </span>
                  </div>
                  <div className="mt-auto pt-2 border-t border-[#f0e8db] flex items-center justify-between">
                    <span className="text-[11px] text-[#8a7963]">Harga Reseller</span>
                    <span className="font-bold text-[#3f2d20]">{idr(it.reseller_price)}</span>
                  </div>
                  <a href={waLink(it)} target="_blank" rel="noreferrer" data-testid={`public-wa-${it.id}`}
                    className="mt-1 w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#25D366] hover:bg-[#1eb955] text-white text-sm font-semibold transition-colors">
                    <MessageCircle className="w-4 h-4" />Pesan via WhatsApp
                  </a>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {data.pages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <Button variant="outline" disabled={data.page <= 1} onClick={() => { setPage((p) => p - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="public-prev" className="bg-white border-[#e3d7c6]">Sebelumnya</Button>
            <span className="text-sm text-[#5a4634]">Hal {data.page} / {data.pages}</span>
            <Button variant="outline" disabled={data.page >= data.pages} onClick={() => { setPage((p) => p + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="public-next" className="bg-white border-[#e3d7c6]">Berikutnya</Button>
          </div>
        )}

        <div className="text-center text-[11px] text-[#b0a08c] mt-8 pb-6">Stok dapat berubah sewaktu-waktu. Hubungi admin untuk pemesanan.</div>
      </main>
    </div>
  );
}
